import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const activityTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Auth version constant - must match across the application
export const AUTH_VERSION = '2.0.0';

export const TokenManager = {
  /**
   * Store authentication tokens in localStorage
   */
  storeTokens: (token: string, expires: string, time_zone: string) => {
    localStorage.setItem("apiToken", token);
    localStorage.setItem("apiTokenExpires", expires);
    localStorage.setItem(
      "lastSignIn",
      dayjs().tz(time_zone || activityTimeZone).format()
    );
    // Set auth version when storing tokens
    localStorage.setItem("authVersion", AUTH_VERSION);
  },

  /**
   * Check if token exists in localStorage
   */
  hasStoredToken: (): boolean => {
    return !!localStorage.getItem("apiToken");
  },

  /**
   * Get stored token from localStorage
   */
  getToken: (): string | null => {
    return localStorage.getItem("apiToken");
  },

  /**
   * Get token expiry from localStorage
   */
  getTokenExpiry: (): string | null => {
    return localStorage.getItem("apiTokenExpires");
  },

  /**
   * Clear all tokens from localStorage
   */
  clearTokens: () => {
    localStorage.removeItem("apiToken");
    localStorage.removeItem("apiTokenExpires");
    localStorage.removeItem("lastSignIn");
  },

  /**
   * Check if currently impersonating
   */
  isImpersonating: (): boolean => {
    return localStorage.getItem("isImpersonating") === "true";
  },

  /**
   * Save original SuperAdmin state before impersonation
   */
  storeImpersonationState: () => {
    const originalState = {
      apiToken: localStorage.getItem("apiToken"),
      apiTokenExpires: localStorage.getItem("apiTokenExpires"),
      userRole: localStorage.getItem("userRole"),
      merchantId: localStorage.getItem("merchantId"),
      subaccountId: localStorage.getItem("subaccountId"),
      merchantName: localStorage.getItem("merchantName"),
      subaccountName: localStorage.getItem("subaccountName"),
    };
    localStorage.setItem("originalSuperAdminState", JSON.stringify(originalState));
  },

  /**
   * Restore original SuperAdmin state and clear impersonation
   */
  restoreOriginalState: () => {
    const stateStr = localStorage.getItem("originalSuperAdminState");
    if (!stateStr) return false;

    try {
      const state = JSON.parse(stateStr);
      localStorage.setItem("apiToken", state.apiToken || "");
      localStorage.setItem("apiTokenExpires", state.apiTokenExpires || "");
      localStorage.setItem("userRole", state.userRole || "");
      if (state.merchantId) {
        localStorage.setItem("merchantId", state.merchantId);
      } else {
        localStorage.removeItem("merchantId");
      }
      if (state.subaccountId) {
        localStorage.setItem("subaccountId", state.subaccountId);
      } else {
        localStorage.removeItem("subaccountId");
      }
      if (state.merchantName) {
        localStorage.setItem("merchantName", state.merchantName);
      } else {
        localStorage.removeItem("merchantName");
      }
      if (state.subaccountName) {
        localStorage.setItem("subaccountName", state.subaccountName);
      } else {
        localStorage.removeItem("subaccountName");
      }
      localStorage.removeItem("isImpersonating");
      localStorage.removeItem("originalSuperAdminState");
      localStorage.removeItem("impersonatingAs");
      localStorage.removeItem("impersonatingName");
      localStorage.removeItem("impersonatingReturnPath");
      return true;
    } catch {
      return false;
    }
  }
};